import type { AxisName } from './types'

/**
 * User-facing copy for the source-derived design-system axes surface
 * (derive-axes → advise → translate-gaps' two axis functions →
 * axis-resolution-list). The pipeline itself is locale-agnostic: every
 * function takes a `DesignAxesCopy` and defaults to `KO_DESIGN_AXES_COPY`
 * so pure callers (WebMCP capability registration, existing tests) keep
 * their behavior, while React surfaces pass the active locale's copy from
 * the studio dictionary (`strings.designSystemAxes`).
 *
 * `Record<AxisName, string>` on `axisLabels`/`adviceWhy` keeps the
 * compile-fails-when-an-axis-is-added guarantee the old inline
 * `satisfies Record<AxisName, string>` tables had.
 */
export interface DesignAxesCopy {
  axisLabels: Record<AxisName, string>
  /** Value column when an axis has no value yet ("정해지지 않음"). */
  notDecidedValue: string
  /**
   * Shown when a control has a value that reaches nothing — measured, not
   * guessed: `summarizeAxisEffect` reports the control decides zero resolved
   * values because another choice took them.
   */
  notInEffect: string
  /** Names the choice that decided instead; receives that control's label. */
  decidedInsteadBy: (axisLabel: string) => string
  /**
   * The positive twin of `notInEffect`: how many resolved values this control
   * is deciding right now, from the same `summarizeAxisEffect` measurement.
   * Six operable controls used to say what they changed only when the answer
   * was "nothing".
   */
  decidesValues: (count: number) => string
  /** Heading of the panel that names values the project never chose. */
  undecidedTitle: string
  /** The fact, with its denominator: how many of how many are at a default. */
  undecidedSummary: (count: number, total: number) => string
  /** Of those, how many no control on this screen can reach at all. */
  undecidedUnreachable: (count: number) => string
  /** Disclosure toggle, closed → open. */
  undecidedShow: string
  /** Disclosure toggle, open → closed. */
  undecidedHide: string
  /** A listed value that no control on this screen decides. */
  undecidedNoControl: string
  /** Empty state: every value in force was chosen by this project. */
  undecidedNone: string
  /** Heading for the theme-wide half of the scope summary. */
  scopeTheme: string
  /** Heading for the per-component half. */
  scopeComponent: string
  /** "N values" for the theme half. */
  scopeThemeValues: (count: number) => string
  /**
   * The component half, with the gap made explicit: how many components this
   * screen reaches, and how many the platform's canonical theme declares.
   */
  scopeComponentReach: (reachable: number, declared: number) => string
  /** Gap explanation: the axis can never be derived from a source. */
  gapCannotRead: string
  /** Gap explanation: no source variable matched this axis. */
  gapNoValueInSource: string
  /** Gap explanation: a source variable matched but was not parseable as a color. */
  gapBaseColorUnparsable: (variablePath: string, rawValue: string) => string
  /** From-source explanation. */
  fromSource: (variablePath: string) => string
  /**
   * Advice row message attached to every gap axis. Receives the PLATFORM
   * DEFAULT that is in force meanwhile.
   *
   * It takes that argument because the sentence that stood here without one
   * was false. It said the value would not reach the app ("이대로 두면 앱에
   * 반영되지 않습니다"), and the code says otherwise: `axesToThemeV2` is total
   * over its input, the store's `designSystem` is never partial (initialised
   * from `DEFAULT_DESIGN_SYSTEM`, and a partial persisted state is REJECTED by
   * `parseDesignSystemState` rather than merged), and that whole object is
   * what auto-save turns into `settings.designSystemTokens` and `tokens`. An
   * undecided axis does not drop out — it ships the default. Naming which
   * default is the difference between a warning and a fact.
   *
   * The neighbouring true claim, that a CHOICE can reach nothing, is a
   * different sentence: `notInEffect`, driven by a measurement.
   */
  adviceNotDecided: (defaultValue: string) => string
  /** Per-axis "why this suggestion" line. */
  adviceWhy: Record<AxisName, string>
  /** ReadinessItem title for a gap axis; receives the localized axis label. */
  notDecidedTitle: (axisLabel: string) => string
  /** ReadinessItem suggestion label pointing at /studio/design-system. */
  decideInDesignSystem: string
  /** ReadinessItem title for an advisory; receives the localized axis label. */
  adviceTitle: (axisLabel: string) => string
  /** Advisory suggestion label; receives the suggested value. */
  applySuggestion: (value: string) => string
  /**
   * Labels for the RADIUS axis's own option values, keyed by the preset name
   * in `@cogo/design-presets`' `RADII_LIST`.
   *
   * Radius is the ONE axis whose option labels are COPY. Every other axis's
   * options are proper names — `Zinc`, `Mauve`, `Noto Sans`, `Jetbrains Mono`
   * — and a font is called `Noto Sans` in every language; those are asserted
   * to stay Latin in `i18n/__tests__/settings-and-design-system-residue.test.tsx`
   * and a gate demanding Korean everywhere would be deleted by the next
   * person. `Default / None / Small / Medium / Large` carry no name at all:
   * they are size adjectives, so they are the one option set that translates.
   *
   * Written out as a union rather than derived as `Record<RadiusKey, …>`
   * because `RADII_LIST` is annotated `readonly RadiusPreset[]`, which widens
   * `RadiusKey` to bare `string` — `Record<string, string>` would accept a
   * table missing every entry. The tie to the real preset table is asserted at
   * runtime instead (`axis-visuals-tooltips.test.tsx`: every `RADII_LIST` name
   * has a label in every locale), so adding a preset upstream turns that red.
   */
  radiusOptionLabels: Record<RadiusOptionName, string>
  /**
   * Swatch tooltips on the two axis pickers (`axis-visuals.tsx`).
   *
   * These are `title` attributes, which makes them HOVER-ONLY: they are not
   * text nodes, so a sweep that walks `textContent` cannot see them, and that
   * is exactly how they survived the lane that localized the surface around
   * them. Any gate over this copy has to walk attributes — see
   * `axis-visuals-tooltips.test.tsx`, which asserts the text-node walk finds
   * nothing here precisely to show why the attribute walk is the load-bearing
   * one.
   */
  swatchTooltips: SwatchTooltipCopy
}

/** Preset names in `RADII_LIST`. See `DesignAxesCopy.radiusOptionLabels`. */
export type RadiusOptionName = 'default' | 'none' | 'small' | 'medium' | 'large'

/**
 * Hover copy for the axis swatches. Each function receives the preset's own
 * NAME or VALUE and frames it — the name itself is never translated, so
 * `Zinc` stays `Zinc` and only the words around it change locale.
 *
 * The font swatch has no entry on purpose: its tooltip is the preset's CSS
 * font stack verbatim (`"Geist", system-ui, sans-serif`), which is the exact
 * string the generated app receives. That is a VALUE, not copy — framing it
 * with a translated noun would make the swatch say something the data does
 * not.
 */
export interface SwatchTooltipCopy {
  /** One dot of the base-colour neutral ramp: the scale's name + its position. */
  baseColorStep: (scaleName: string, step: number) => string
  /** The theme's primary hue as it renders on a light background. */
  themeLight: (themeName: string) => string
  /** The theme's primary hue as it renders on a dark background. */
  themeDark: (themeName: string) => string
  /** The radius corner square: the localized label + the CSS length it carries. */
  radius: (label: string, cssValue: string) => string
}

/**
 * The single reader of `radiusOptionLabels` — every surface that renders a
 * radius option goes through here rather than through `option.label`, so
 * there is one place to look when asking whether the localized label is
 * actually reaching the screen.
 *
 * Falls back to the preset's own English label rather than to the raw value:
 * an unlabelled preset should read `Large`, never `large`.
 */
export function radiusOptionLabel(
  option: { value: string; label: string },
  copy: DesignAxesCopy,
): string {
  return copy.radiusOptionLabels[option.value as RadiusOptionName] ?? option.label
}

/**
 * 받침 유무에 따라 "이"/"가" 주격 조사를 고른다 (translate-gaps.ts 에서 이동).
 * 완성형 한글 음절 전용 — 그 밖의 값은 "가"로 안전하게 폴백한다.
 */
export function withSubjectParticle(word: string): string {
  const HANGUL_BASE = 0xac00
  const HANGUL_LAST = 0xd7a3
  const lastChar = word.at(-1) ?? ''
  const code = lastChar.codePointAt(0) ?? 0
  if (code < HANGUL_BASE || code > HANGUL_LAST) return `${word}가`
  const hasBatchim = (code - HANGUL_BASE) % 28 !== 0
  return hasBatchim ? `${word}이` : `${word}가`
}

/**
 * `radius` 라벨은 "반경"도 "모서리"도 아닌 "모서리 반경" — 두 표현이 가리키는
 * 개념이 같고(둘 다 border-radius), 한국어 디자인 툴 로캘라이즈에서 흔히
 * 쓰이는 합성어라 어느 한쪽만 쓰는 것보다 실제 사용자에게 더 또렷하다.
 * (translate-gaps.ts 의 기존 결정 그대로.)
 */
export const KO_DESIGN_AXES_COPY: DesignAxesCopy = {
  axisLabels: {
    style: '스타일',
    baseColor: '기본 색',
    theme: '테마',
    chart: '차트 색',
    radius: '모서리 반경',
    font: '글꼴',
  },
  notDecidedValue: '정해지지 않음',
  notInEffect: '이 선택은 지금 적용되지 않습니다.',
  scopeTheme: '테마 전체',
  scopeComponent: '컴포넌트별',
  scopeThemeValues: (count) => `값 ${count}개`,
  scopeComponentReach: (reachable, declared) =>
    `이 화면에서 정할 수 있는 컴포넌트 ${reachable}개 · 시스템이 선언한 것은 ${declared}개`,
  decidedInsteadBy: (axisLabel) => `${axisLabel} 선택이 이 값을 결정합니다.`,
  decidesValues: (count) => `지금 이 화면의 값 ${count}개를 결정하고 있습니다.`,
  undecidedTitle: '이 프로젝트가 정하지 않은 값',
  undecidedSummary: (count, total) =>
    `전체 ${total}개 중 ${count}개는 프로젝트가 정한 적이 없어 기본값이 그대로 적용되고 있습니다.`,
  undecidedUnreachable: (count) =>
    `그중 ${count}개는 이 화면의 어떤 컨트롤로도 정할 수 없습니다.`,
  undecidedShow: '어떤 값인지 보기',
  undecidedHide: '접기',
  undecidedNoControl: '이 화면에 컨트롤 없음',
  undecidedNone: '기본값으로 남은 값이 없습니다 — 지금 적용되는 값은 모두 이 프로젝트가 정했습니다.',
  gapCannotRead: '이 값은 소스에서 읽을 수 없습니다. 정해 주세요.',
  gapNoValueInSource: '소스에 해당하는 값이 없습니다. 정해 주세요.',
  gapBaseColorUnparsable: (variablePath, rawValue) =>
    `소스의 ${variablePath} 값(${rawValue})을 색상으로 해석할 수 없어 baseColor 를 채우지 못했습니다. 정해 주세요.`,
  fromSource: (variablePath) => `소스의 ${variablePath} 에서 가져왔습니다.`,
  adviceNotDecided: (defaultValue) =>
    `아직 정하지 않았습니다. 정할 때까지 기본값(${defaultValue})이 그대로 적용되고, 생성되는 앱에도 그 값이 들어갑니다.`,
  adviceWhy: {
    style: '가장 널리 쓰이는 기본 스타일입니다.',
    baseColor: '중립 팔레트라 어떤 브랜드 색과도 부딪히지 않습니다.',
    theme: '기본 테마는 밝은 배경과 어두운 배경을 모두 지원합니다.',
    chart: '차트 색은 서로 구분되도록 고른 조합입니다.',
    radius: '기본 반경은 대부분의 컴포넌트에서 자연스럽습니다.',
    font: '본문 가독성이 검증된 글꼴입니다.',
  },
  notDecidedTitle: (axisLabel) => `${withSubjectParticle(axisLabel)} 정해지지 않았습니다`,
  decideInDesignSystem: '디자인시스템에서 정하기',
  adviceTitle: (axisLabel) => `${axisLabel}: 권고`,
  applySuggestion: (value) => `${value} 적용`,
  radiusOptionLabels: {
    default: '기본',
    none: '없음',
    small: '작게',
    medium: '보통',
    large: '크게',
  },
  swatchTooltips: {
    baseColorStep: (scaleName, step) => `${scaleName} ${step}단계`,
    themeLight: (themeName) => `${themeName} 밝은 배경`,
    themeDark: (themeName) => `${themeName} 어두운 배경`,
    radius: (label, cssValue) => `${label} (${cssValue})`,
  },
}

export const EN_DESIGN_AXES_COPY: DesignAxesCopy = {
  axisLabels: {
    style: 'Style',
    baseColor: 'Base color',
    theme: 'Theme',
    chart: 'Chart colors',
    radius: 'Corner radius',
    font: 'Font',
  },
  notDecidedValue: 'Not decided yet',
  notInEffect: 'This choice is not in effect.',
  scopeTheme: 'Theme-wide',
  scopeComponent: 'Per component',
  scopeThemeValues: (count) => `${count} values`,
  scopeComponentReach: (reachable, declared) =>
    `${reachable} components decided here · ${declared} declared by the system`,
  decidedInsteadBy: (axisLabel) => `The ${axisLabel} choice decides this value.`,
  decidesValues: (count) => `Deciding ${count} values on this screen right now.`,
  undecidedTitle: 'Values this project never decided',
  undecidedSummary: (count, total) =>
    `${count} of ${total} values are in force at their default because this project never chose them.`,
  undecidedUnreachable: (count) =>
    `${count} of those cannot be decided by any control on this screen.`,
  undecidedShow: 'See which values',
  undecidedHide: 'Hide',
  undecidedNoControl: 'No control here',
  undecidedNone:
    'Nothing is left at a default — every value in force was chosen by this project.',
  gapCannotRead: 'This value cannot be read from a source. Choose one.',
  gapNoValueInSource: 'The source has no matching value. Choose one.',
  gapBaseColorUnparsable: (variablePath, rawValue) =>
    `The source value ${rawValue} at ${variablePath} could not be read as a color, so the base color is still open. Choose one.`,
  fromSource: (variablePath) => `Read from ${variablePath} in your source.`,
  adviceNotDecided: (defaultValue) =>
    `Not decided yet. Until you choose, the default (${defaultValue}) stays in force, and generated apps carry that value.`,
  adviceWhy: {
    style: 'The most widely used default style.',
    baseColor: 'A neutral palette that will not clash with any brand color.',
    theme: 'The default theme supports both light and dark backgrounds.',
    chart: 'Chart colors picked to stay distinguishable from each other.',
    radius: 'The default radius feels natural on most components.',
    font: 'A typeface with proven body-text readability.',
  },
  notDecidedTitle: (axisLabel) => `${axisLabel} has not been decided yet`,
  decideInDesignSystem: 'Decide in Design System',
  adviceTitle: (axisLabel) => `${axisLabel}: suggestion`,
  applySuggestion: (value) => `Apply ${value}`,
  radiusOptionLabels: {
    default: 'Default',
    none: 'None',
    small: 'Small',
    medium: 'Medium',
    large: 'Large',
  },
  swatchTooltips: {
    baseColorStep: (scaleName, step) => `${scaleName} step ${step}`,
    themeLight: (themeName) => `${themeName} on a light background`,
    themeDark: (themeName) => `${themeName} on a dark background`,
    radius: (label, cssValue) => `${label} (${cssValue})`,
  },
}

export const JA_DESIGN_AXES_COPY: DesignAxesCopy = {
  axisLabels: {
    style: 'スタイル',
    baseColor: 'ベースカラー',
    theme: 'テーマ',
    chart: 'チャート色',
    radius: '角丸',
    font: 'フォント',
  },
  notDecidedValue: '未設定',
  notInEffect: 'この選択は現在適用されていません。',
  scopeTheme: 'テーマ全体',
  scopeComponent: 'コンポーネント別',
  scopeThemeValues: (count) => `値 ${count}件`,
  scopeComponentReach: (reachable, declared) =>
    `この画面で決められるコンポーネント ${reachable}件 · システムの宣言は ${declared}件`,
  decidedInsteadBy: (axisLabel) => `${axisLabel} の選択がこの値を決めています。`,
  decidesValues: (count) => `現在この画面の値 ${count} 件を決めています。`,
  undecidedTitle: 'このプロジェクトが決めていない値',
  undecidedSummary: (count, total) =>
    `全 ${total} 件のうち ${count} 件は、プロジェクトが決めていないため既定値がそのまま適用されています。`,
  undecidedUnreachable: (count) =>
    `そのうち ${count} 件はこの画面のどのコントロールでも決められません。`,
  undecidedShow: 'どの値か見る',
  undecidedHide: '閉じる',
  undecidedNoControl: 'この画面にコントロールなし',
  undecidedNone:
    '既定値のまま残っている値はありません — 適用中の値はすべてこのプロジェクトが決めたものです。',
  gapCannotRead: 'この値はソースから読み取れません。選択してください。',
  gapNoValueInSource: 'ソースに該当する値がありません。選択してください。',
  gapBaseColorUnparsable: (variablePath, rawValue) =>
    `ソースの ${variablePath} の値（${rawValue}）を色として解釈できなかったため、ベースカラーは未設定のままです。選択してください。`,
  fromSource: (variablePath) => `ソースの ${variablePath} から読み取りました。`,
  adviceNotDecided: (defaultValue) =>
    `未設定です。選ぶまでは既定値（${defaultValue}）がそのまま適用され、生成されるアプリにもその値が入ります。`,
  adviceWhy: {
    style: '最も広く使われる標準スタイルです。',
    baseColor: 'ニュートラルパレットなのでどのブランドカラーとも衝突しません。',
    theme: 'デフォルトテーマは明るい背景と暗い背景の両方に対応します。',
    chart: 'チャート色は互いに区別しやすい組み合わせです。',
    radius: 'デフォルトの角丸はほとんどのコンポーネントで自然に見えます。',
    font: '本文の可読性が検証されたフォントです。',
  },
  notDecidedTitle: (axisLabel) => `${axisLabel}が未設定です`,
  decideInDesignSystem: 'デザインシステムで設定',
  adviceTitle: (axisLabel) => `${axisLabel}: 提案`,
  applySuggestion: (value) => `${value} を適用`,
  radiusOptionLabels: {
    default: '標準',
    none: 'なし',
    small: '小さめ',
    medium: '中くらい',
    large: '大きめ',
  },
  swatchTooltips: {
    baseColorStep: (scaleName, step) => `${scaleName} 第${step}段階`,
    themeLight: (themeName) => `${themeName} 明るい背景`,
    themeDark: (themeName) => `${themeName} 暗い背景`,
    radius: (label, cssValue) => `${label}（${cssValue}）`,
  },
}

export const ZH_CN_DESIGN_AXES_COPY: DesignAxesCopy = {
  axisLabels: {
    style: '样式',
    baseColor: '基础色',
    theme: '主题',
    chart: '图表配色',
    radius: '圆角',
    font: '字体',
  },
  notDecidedValue: '尚未设定',
  notInEffect: '此选择当前未生效。',
  scopeTheme: '主题整体',
  scopeComponent: '按组件',
  scopeThemeValues: (count) => `${count} 个值`,
  scopeComponentReach: (reachable, declared) =>
    `此页面可决定的组件 ${reachable} 个 · 系统声明 ${declared} 个`,
  decidedInsteadBy: (axisLabel) => `由${axisLabel}的选择决定此值。`,
  decidesValues: (count) => `当前决定着此页面的 ${count} 个值。`,
  undecidedTitle: '此项目未设定的值',
  undecidedSummary: (count, total) =>
    `共 ${total} 个值中有 ${count} 个从未由项目设定，正沿用默认值。`,
  undecidedUnreachable: (count) => `其中 ${count} 个无法通过此页面的任何控件设定。`,
  undecidedShow: '查看是哪些值',
  undecidedHide: '收起',
  undecidedNoControl: '此页面无对应控件',
  undecidedNone: '没有值停留在默认状态 — 当前生效的每个值都由此项目设定。',
  gapCannotRead: '无法从来源读取此值。请选择。',
  gapNoValueInSource: '来源中没有对应的值。请选择。',
  gapBaseColorUnparsable: (variablePath, rawValue) =>
    `来源 ${variablePath} 的值（${rawValue}）无法解析为颜色，基础色仍未设定。请选择。`,
  fromSource: (variablePath) => `读取自来源 ${variablePath}。`,
  adviceNotDecided: (defaultValue) =>
    `尚未设定。在你选择之前将沿用默认值（${defaultValue}），生成的应用也会带上该值。`,
  adviceWhy: {
    style: '最广泛使用的默认样式。',
    baseColor: '中性调色板，不会与任何品牌色冲突。',
    theme: '默认主题同时支持浅色和深色背景。',
    chart: '图表配色经过挑选，彼此易于区分。',
    radius: '默认圆角在大多数组件上都很自然。',
    font: '正文可读性经过验证的字体。',
  },
  notDecidedTitle: (axisLabel) => `${axisLabel}尚未设定`,
  decideInDesignSystem: '在设计系统中设定',
  adviceTitle: (axisLabel) => `${axisLabel}：建议`,
  applySuggestion: (value) => `应用 ${value}`,
  radiusOptionLabels: {
    default: '默认',
    none: '无',
    small: '小',
    medium: '中',
    large: '大',
  },
  swatchTooltips: {
    baseColorStep: (scaleName, step) => `${scaleName} 第 ${step} 阶`,
    themeLight: (themeName) => `${themeName} 浅色背景`,
    themeDark: (themeName) => `${themeName} 深色背景`,
    radius: (label, cssValue) => `${label}（${cssValue}）`,
  },
}

export const ZH_TW_DESIGN_AXES_COPY: DesignAxesCopy = {
  axisLabels: {
    style: '樣式',
    baseColor: '基礎色',
    theme: '主題',
    chart: '圖表配色',
    radius: '圓角',
    font: '字型',
  },
  notDecidedValue: '尚未設定',
  notInEffect: '此選擇目前未生效。',
  scopeTheme: '主題整體',
  scopeComponent: '按元件',
  scopeThemeValues: (count) => `${count} 個值`,
  scopeComponentReach: (reachable, declared) =>
    `此頁面可決定的元件 ${reachable} 個 · 系統宣告 ${declared} 個`,
  decidedInsteadBy: (axisLabel) => `由${axisLabel}的選擇決定此值。`,
  decidesValues: (count) => `目前決定著此頁面的 ${count} 個值。`,
  undecidedTitle: '此專案未設定的值',
  undecidedSummary: (count, total) =>
    `共 ${total} 個值中有 ${count} 個從未由專案設定，正沿用預設值。`,
  undecidedUnreachable: (count) => `其中 ${count} 個無法透過此頁面的任何控制項設定。`,
  undecidedShow: '查看是哪些值',
  undecidedHide: '收合',
  undecidedNoControl: '此頁面無對應控制項',
  undecidedNone: '沒有值停留在預設狀態 — 目前生效的每個值都由此專案設定。',
  gapCannotRead: '無法從來源讀取此值。請選擇。',
  gapNoValueInSource: '來源中沒有對應的值。請選擇。',
  gapBaseColorUnparsable: (variablePath, rawValue) =>
    `來源 ${variablePath} 的值（${rawValue}）無法解析為顏色，基礎色仍未設定。請選擇。`,
  fromSource: (variablePath) => `讀取自來源 ${variablePath}。`,
  adviceNotDecided: (defaultValue) =>
    `尚未設定。在你選擇之前將沿用預設值（${defaultValue}），產生的應用程式也會帶上該值。`,
  adviceWhy: {
    style: '最廣泛使用的預設樣式。',
    baseColor: '中性調色盤，不會與任何品牌色衝突。',
    theme: '預設主題同時支援淺色與深色背景。',
    chart: '圖表配色經過挑選，彼此容易區分。',
    radius: '預設圓角在大多數元件上都很自然。',
    font: '內文可讀性經過驗證的字型。',
  },
  notDecidedTitle: (axisLabel) => `${axisLabel}尚未設定`,
  decideInDesignSystem: '在設計系統中設定',
  adviceTitle: (axisLabel) => `${axisLabel}：建議`,
  applySuggestion: (value) => `套用 ${value}`,
  radiusOptionLabels: {
    default: '預設',
    none: '無',
    small: '小',
    medium: '中',
    large: '大',
  },
  swatchTooltips: {
    baseColorStep: (scaleName, step) => `${scaleName} 第 ${step} 階`,
    themeLight: (themeName) => `${themeName} 淺色背景`,
    themeDark: (themeName) => `${themeName} 深色背景`,
    radius: (label, cssValue) => `${label}（${cssValue}）`,
  },
}
